import { useTranslation } from '../../../lib/i18n';
import { nameOf, type BrowsableFile } from '../../../lib/file-browser';
import { useFileDelete } from '../../../lib/use-file-delete';
import { Modal } from '../../Modal';
import { Button, Typography } from '../ui';

// The confirm in front of "Delete" on a MEMORY row — the one destructive action a Files screen offers.
//
// Forgetting is irreversible (the file-system keeps no revisions), so it takes the same shape every other
// destructive action here does (see `CancelTaskConfirm`): a named confirm rather than a bare OK/Cancel
// pair, the destructive skin on the button that ends something, and never `window.confirm`, which cannot
// be styled, translated consistently, or kept from being dismissed by a keypress.
//
// The words are the contract shared with both phones: "Forget this? Symphony will no longer remember it."
// — it is the AGENT that forgets, and that is what the reader is deciding.
export function ForgetMemoryDialog({
  item,
  onClose,
  onForgotten,
}: {
  item: BrowsableFile;
  onClose: () => void;
  /** The memory is gone and the list has been asked to re-read. The host says so (the toast). */
  onForgotten: () => void;
}) {
  const { t } = useTranslation();
  const { state, remove } = useFileDelete();
  const busy = state === 'deleting';

  const confirm = async () => {
    if (await remove(item)) {
      onForgotten();
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      width={440}
      maxHeightPx={650}
      scrollBody
      title={nameOf(item.name)}
      footer={
        <>
          <Button variant="outlined" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button color="destructive" loading={busy} onClick={() => void confirm()}>
            {t('files.action.delete')}
          </Button>
        </>
      }
    >
      <Typography variant="p2" color="secondary" component="p">
        {t('files.action.deleteConfirm')}
      </Typography>
      {state === 'error' && (
        <Typography variant="p2" component="p">
          {t('files.action.deleteFailed')}
        </Typography>
      )}
    </Modal>
  );
}
